import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ImportTab() {
  const fileRef = useRef();
  const [supplier, setSupplier] = useState("");
  const [gst, setGst] = useState("exclusive");
  const [meaning, setMeaning] = useState("cost");
  const [limit, setLimit] = useState("");
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState([]);

  const load = () => api.get("/admin/import-logs").then((r) => setLogs(r.data));
  useEffect(() => { load(); }, []);
  const pmRef = useRef();
  const [pmBusy, setPmBusy] = useState(false);

  const submitPM = async () => {
    const file = pmRef.current?.files?.[0];
    if (!file) return toast.error("Choose a competitor export CSV first");
    setPmBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("acp_token");
      const res = await fetch(`${API}/admin/price-match`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Price match failed");
      toast.success(`Price matched ${data.matched} products ($1 under competitor). ${data.not_found} SKUs not in catalog.`);
      pmRef.current.value = "";
      load();
    } catch (e) { toast.error(e.message); }
    finally { setPmBusy(false); }
  };

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("supplier", supplier);
    fd.append("gst_treatment", gst);
    fd.append("price_meaning", meaning);
    fd.append("limit", limit || "0");
    try {
      const token = localStorage.getItem("acp_token");
      const res = await fetch(`${API}/admin/import`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(`Imported: ${data.created} new, ${data.updated} updated, ${data.held} held for review`);
      fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-1">Supplier Import</h1>
      <p className="text-muted-foreground mb-6">Upload a supplier / dealer price feed. Columns are auto-mapped (SKU, Mftr Code, Price, Qty, Category, Image URL, etc.).</p>

      <div className="rounded-xl border bg-white p-6 max-w-2xl space-y-4">
        <div className="border-2 border-dashed rounded-xl p-8 text-center">
          <Upload className="mx-auto text-accent mb-2" />
          <input data-testid="import-file-input" ref={fileRef} type="file" accept=".csv" className="mx-auto text-sm" />
          <p className="text-xs text-muted-foreground mt-2">CSV up to any size. Use the row limit for a quick test import.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Default Supplier Name</Label><Input data-testid="import-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. Dove Electronics" /></div>
          <div><Label>Row Limit (0 = all)</Label><Input data-testid="import-limit" type="number" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="0" /></div>
          <div>
            <Label>Supplier Prices Are</Label>
            <Select value={gst} onValueChange={setGst}>
              <SelectTrigger data-testid="import-gst"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="exclusive">GST Exclusive</SelectItem>
                <SelectItem value="inclusive">GST Inclusive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Price Column Represents</Label>
            <Select value={meaning} onValueChange={setMeaning}>
              <SelectTrigger data-testid="import-meaning"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cost">Supplier cost (apply markup)</SelectItem>
                <SelectItem value="retail_incl">Final retail price (incl GST)</SelectItem>
                <SelectItem value="retail_excl">Retail price (ex GST)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="run-import" onClick={submit} disabled={busy} className="w-full bg-steel text-white hover:bg-steel-light">
          {busy ? "Importing…" : "Run Import"}
        </Button>
      </div>

      <div className="rounded-xl border bg-white p-6 max-w-2xl space-y-4 mt-6">
        <div>
          <h2 className="font-heading font-bold text-lg">Competitor Price Match</h2>
          <p className="text-sm text-muted-foreground">Upload a competitor product export. Any SKU that matches an ACP product is automatically priced <strong>$1.00 cheaper (incl GST)</strong> than the competitor. The saving shows in red on the storefront. Non-matching products are untouched.</p>
        </div>
        <div className="border-2 border-dashed rounded-xl p-6 text-center">
          <input data-testid="pricematch-file-input" ref={pmRef} type="file" accept=".csv" className="mx-auto text-sm" />
        </div>
        <Button data-testid="run-pricematch" onClick={submitPM} disabled={pmBusy} className="w-full bg-red-600 text-white hover:bg-red-700">
          {pmBusy ? "Matching…" : "Run Price Match"}
        </Button>
      </div>

      <h2 className="font-heading font-bold text-lg mt-8 mb-3">Import History</h2>
      <div className="rounded-xl border bg-white divide-y">
        {logs.length === 0 && <p className="p-4 text-sm text-muted-foreground">No imports yet.</p>}
        {logs.map((l) => (
          <div key={l.id} className="p-4 flex items-center gap-3 text-sm">
            <FileText size={16} className="text-muted-foreground shrink-0" />
            <span className="font-mono truncate flex-1">{l.filename}</span>
            <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={14} /> {l.created} new</span>
            <span className="text-steel">{l.updated} updated</span>
            {l.held > 0 && <span className="text-destructive">{l.held} held</span>}
            <span className="text-muted-foreground">{new Date(l.timestamp).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
